import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Platform } from 'react-native';

import { getCustomerCardsQuery } from '@/api/customer/getCustomerCardsQuery';
import type { Shop } from '@/types/appointment';
import type { Cart } from '@/types/booking';
import type { MethodsGroup } from '@/utils/getPaymentMethodsOrder';
import { getPaymentMethodsOrder } from '@/utils/getPaymentMethodsOrder';
import { isNative } from '@/utils/getPlatform';
import { getShouldShowPayInPerson } from '@/utils/getShouldShowPayInPerson';
import { Logger } from '@/utils/logging';
import {
  PAYMENT_METHOD_APPLE_PAY,
  PAYMENT_METHOD_CARD_GENERIC,
  PAYMENT_METHOD_CASH_APP,
  PAYMENT_METHOD_GOOGLE_PAY,
  PAYMENT_METHOD_PAY_IN_PERSON,
  type PaymentMethod,
  createBnplPaymentMethod,
} from '@/utils/paymentMethods';

import { useCustomerQuery } from '../queries/customer/useCustomerQuery';
import { useBnplEligibility } from './useBnplEligibility';
import { useCashAppEligibility } from './useCashAppEligibility';
import { useDigitalWalletPay } from './useDigitalWalletPay';

export const useAvailablePaymentMethods = (
  cart: Cart | null | undefined,
  shop: Shop,
  isWaitlist: boolean,
  options?: { includeSavedCards?: boolean },
) => {
  const includeSavedCards = options?.includeSavedCards ?? true;
  const { data: customer } = useCustomerQuery();

  const { isAvailable: isApplePayAvailable } = useDigitalWalletPay('applePay');
  const { isAvailable: isGooglePayAvailable } =
    useDigitalWalletPay('googlePay');

  const { eligibleMethods: eligibleBnplMethods } = useBnplEligibility(
    cart,
    shop,
  );

  const isCashAppEligible = useCashAppEligibility(cart);

  // A link holder must never see another customer's (or their own, unrelated)
  // saved cards here — the public accept refuses card_on_file (contract SQR-16460).
  const cardsCustomerId = includeSavedCards ? customer?.id : undefined;
  const { data: userCards } = useSuspenseQuery(
    getCustomerCardsQuery(cardsCustomerId ?? ''),
  );

  const isNativePlatform = isNative();

  const methods = useMemo(() => {
    // Check conditions for pay-in-person availability
    const { canBookNoPay, canReserve } = cart?.breakdown ?? {};
    const isCoveredByGiftCard =
      !!cart?.breakdown?.discountAmount &&
      cart?.breakdown?.outstandingChargeAmount === 0;
    // Retained for telemetry only — no longer gates PiP visibility (SQR-13019).
    const hasMultipleAppointments = (cart?.new_appointments?.length ?? 0) >= 2;

    const shouldShowPayInPerson = getShouldShowPayInPerson({
      breakdown: cart?.breakdown,
      isWaitlist,
      isCoveredByGiftCard,
    });

    // Log if shop allows pay-in-person but the button is hidden
    try {
      const shopAllowsInPersonPayments = shop?.allowInPersonPayments;
      const shopRequiresPrepayment = shop?.requiresPrepayment;

      // Waitlists, gift-card-covered carts and carts still waiting for their
      // breakdown hide the button by design; only the rest is the open case.
      const isExplained = isWaitlist || isCoveredByGiftCard || !cart?.breakdown;
      if (
        shopAllowsInPersonPayments &&
        !shopRequiresPrepayment &&
        !shouldShowPayInPerson &&
        !isExplained
      ) {
        Logger.warn(
          '[BT-10271] MOBILE-WIDGET: Pay-in-person button filtered out in usePaymentMethods',
          {
            cartId: cart?.id,

            // Filtering conditions that caused the issue
            filteringConditions: {
              canBookNoPay,
              canReserve,
              hasBreakdown: !!cart?.breakdown,
              isCoveredByGiftCard,
              isWaitlist,
              // observational only — SQR-13019 removed the multi-appointment guard
              hasMultipleAppointments,
              shopAllowsInPersonPayments,
              shopRequiresPrepayment,
            },

            // Client environment for correlation
            clientInfo: Platform.select({
              web: {
                userAgent:
                  typeof window !== 'undefined'
                    ? (window.navigator?.userAgent ?? 'unknown')
                    : 'unknown',

                timestamp: new Date().toISOString(),
                referrer:
                  typeof document !== 'undefined' ? document.referrer : null,
                url:
                  typeof window !== 'undefined'
                    ? (window.location?.href ?? null)
                    : null,
              },
              default: {
                userAgent: 'squire-mobile-flagship',
                timestamp: new Date().toISOString(),
                referrer: null,
                url: null,
              },
            }),
            // Source file identification
            sourceFile: 'src/hooks/payments/useAvailablePaymentMethods.ts',
          },
        );
      }
    } catch {
      // do nothing
    }

    const methodsByGroup: {
      group: MethodsGroup;
      methods: (PaymentMethod | undefined)[];
    }[] = [
      {
        group: 'applePay',
        methods: [
          isApplePayAvailable ? PAYMENT_METHOD_APPLE_PAY : undefined,
          isGooglePayAvailable ? PAYMENT_METHOD_GOOGLE_PAY : undefined,
        ],
      },
      {
        group: 'card',
        methods: [PAYMENT_METHOD_CARD_GENERIC],
      },
      {
        group: 'payInPerson',
        methods:
          shouldShowPayInPerson && !isNativePlatform
            ? [PAYMENT_METHOD_PAY_IN_PERSON]
            : [],
      },
      {
        group: 'cashApp',
        // SQR-15345: never offered for waitlist joins, matching WoM mobile
        // (PMNTS-2815). The backend session has no waitlist awareness.
        methods: [
          isCashAppEligible && !isWaitlist
            ? PAYMENT_METHOD_CASH_APP
            : undefined,
        ],
      },
      {
        group: 'bnpl',
        // SQR-15886: one row per provider, in the order the payment session
        // returns them. Never offered for waitlist joins.
        methods: isWaitlist
          ? []
          : eligibleBnplMethods.map((provider) =>
              createBnplPaymentMethod([provider]),
            ),
      },
    ];

    const methodsOrder = getPaymentMethodsOrder(shop?.brand?.id);

    const sortedMethods = methodsByGroup
      .map((item) => ({ ...item, idx: methodsOrder.indexOf(item.group) }))
      .sort(({ idx: idxA }, { idx: idxB }) => {
        // if group is missing in the `methodsOrder`, it will be listed last
        const notFoundIdx = 255;
        return (
          (idxA >= 0 ? idxA : notFoundIdx) - (idxB >= 0 ? idxB : notFoundIdx)
        );
      })
      .reduce(
        (memo, item) => [
          ...memo,
          ...(item.methods.filter(Boolean) as PaymentMethod[]),
        ],
        [] as PaymentMethod[],
      )
      .filter(Boolean);

    // saved cards always go first
    return [...(userCards ?? []), ...sortedMethods];
  }, [
    cart,
    isWaitlist,
    isNativePlatform,
    isApplePayAvailable,
    isGooglePayAvailable,
    eligibleBnplMethods,
    isCashAppEligible,
    shop?.brand?.id,
    shop?.allowInPersonPayments,
    shop?.requiresPrepayment,
    userCards,
  ]);

  return methods;
};
